import { Currency } from "@bentolabs/kit/entities";
import { useMutation } from "@tanstack/react-query";
import invariant from "tiny-invariant";
import { getContract, parseUnits } from "viem";
import { waitForTransactionReceipt } from "viem/actions";
import { useAccount, useChainId, useWalletClient } from "wagmi";

import { tellerABI } from "shared/config/abi";
import { bentoUSDConfig, tellerConfig } from "shared/config/const";
import { calculateMinAmount } from "widgets/trade/utils";

type Params = {
  shareCurrency: Currency;
  currency: Currency;
  currencyAmount: string;
  shareAmount: string;
  slippage: string;
};

export const useWithdrawAssetMutation = () => {
  const chainId = useChainId();
  const { data: wc } = useWalletClient();
  const { address } = useAccount();
  return useMutation({
    mutationFn: async ({
      currency,
      shareCurrency,
      currencyAmount,
      shareAmount,
      slippage,
    }: Params) => {
      invariant(wc);
      invariant(address);
      const contract = getContract({
        abi: tellerABI,
        address: tellerConfig[chainId],
        client: wc,
      });
      const preparedShareAmount = parseUnits(
        shareAmount,
        shareCurrency.decimals
      );
      const minimumAssets = calculateMinAmount(currencyAmount, slippage);
      const preparedMinimumAssets = parseUnits(
        minimumAssets,
        currency.decimals
      );

      const logParam = {
        method: "bulkWithdraw",
        contractName: "teller",
        contractAddress: tellerConfig[chainId],
        spenderContract: `${bentoUSDConfig[chainId].address}`,
        spenderContractName: `BoringVault`,
        requestArgs: [
          currency.address,
          preparedShareAmount.toString(),
          preparedMinimumAssets.toString(),
          address,
        ],
        withdrawAsset: `${currency.address} ${currency.symbol}`,
        shareAmount: `${shareAmount} ${shareCurrency.symbol}`,
        minimumAssets: `${minimumAssets} ${currency.symbol}`,
        to: address,
      };
      console.log("Instant Withdraw TX: ", logParam);
      console.log(JSON.stringify(logParam, null, 2));

      const hash = await contract.write.bulkWithdraw([
        currency.address,
        preparedShareAmount,
        preparedMinimumAssets,
        address,
      ]);

      const receipt = await waitForTransactionReceipt(wc, { hash });

      if (receipt.status === "reverted") {
        throw new Error("Transaction has been reverted");
      }

      return receipt;
    },
  });
};
